import { useFragment } from '@apollo/client';
import { Button } from '@synoptic/ui-kit/button/button.tsx';
import { NoData } from '@synoptic/ui-kit/no-data/no-data.tsx';
import { useParams } from 'react-router';
import { gql } from '@/__generated__';
import { Link } from '@/routes-utils/navigation.tsx';
import { paths } from '@/routes-utils/paths.ts';
import { useIsMe } from '../../../use-is-me.ts';
import {
  blockHeading,
  detailedStatisticsBlock,
  detailedStatisticsSection,
} from '../detailed-statistics.css.ts';
import { AwardDetails } from './award-details.tsx';
import { MedalsBlock } from './medal-block.tsx';
import { tournamentsEmpty } from './tournaments-section.css.ts';

const UserTournamentsSection = gql(`
  fragment UserTournamentsSection on User {
    medals {
      medal {
        ...AwardFragment
      }
      tournaments{
        id
        name
      }
    }
  }
`);

export const TournamentsSection = ({ userId }: { userId: string }) => {
  const isMe = useIsMe();
  const { username } = useParams();

  const { data, complete } = useFragment({
    fragment: UserTournamentsSection,
    fragmentName: 'UserTournamentsSection',
    from: {
      __typename: 'User',
      id: userId,
    },
  });

  if (!complete) return null;

  const tournaments = data.medals.flatMap(({ medal, tournaments }) =>
    tournaments.map((tournament) => ({
      medal,
      tournament,
    })),
  );

  return (
    <div className={detailedStatisticsSection}>
      <div className={detailedStatisticsBlock}>
        <span className={blockHeading}>Tournaments</span>
        {(data.medals || []).length === 0 ? (
          <div className={tournamentsEmpty}>
            <NoData>
              {isMe ? "You haven't" : `${username} hasn't`} won any tournaments
              yet.
            </NoData>
            {isMe ? (
              <Button size={'small'} variant="outline" asChild>
                <Link to={paths.tournaments}>Explore tournaments</Link>
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            <MedalsBlock medals={data.medals} />
            {tournaments.map(({ medal, tournament }) => (
              <AwardDetails
                key={tournament.id}
                medalName={medal.name as string}
                tournamentId={tournament.id}
                tournamentName={tournament.name as string}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};
